import type { ParagraphProductCta as ParagraphProductCtaData } from "@/lib/api/cms-catalog";
import type { ParagraphTranslations } from "../Paragraph.i18n";
import { HtmlText } from "../shared/HtmlText";
import styles from "./ParagraphProductCta.module.css";

/**
 * Layout 34 - `PARAGRAPH_PRODUCT_CTA_LAYOUT`: produkt CTA z gridem text/image.
 *
 * Struktura:
 *   - Grid 1.15fr content | 1fr image (light mint bg + border)
 *   - Content column (lewa):
 *       - Badge (text_1) - mala pill z borderem, nad tytulem
 *       - Title h2 (name) - plain text
 *       - Desc (text) - HTML z TinyMCE
 *       - Actions row:
 *           - Primary CTA (button_name/button_url) - solid accent + strzalka
 *           - Ghost button z tag'iem (ghost_label + ghost_tag) - dashed border,
 *             hover pokazuje tooltip (tooltip_desc + tooltip_cta)
 *   - Image column (prawa) - zdjecie produktu
 */
export function ParagraphProductCta({
  paragraph,
}: {
  paragraph: ParagraphProductCtaData;
  t: ParagraphTranslations;
}) {
  const {
    text_1: badge,
    name: title,
    text: desc,
    button_name: primaryLabel,
    button_url: primaryUrl,
    image,
    ghost_label,
    ghost_tag,
    tooltip_desc,
    tooltip_cta_label,
    tooltip_cta_url,
  } = paragraph;

  const hasBadge = badge && badge.trim() !== "";
  const hasDesc = desc && desc.trim() !== "";
  const hasPrimary = primaryLabel && primaryLabel.trim() !== "" && primaryUrl && primaryUrl.trim() !== "";
  const hasGhost = ghost_label && ghost_label.trim() !== "";
  const hasTooltipCta = tooltip_cta_label && tooltip_cta_label.trim() !== "" && tooltip_cta_url && tooltip_cta_url.trim() !== "";
  const hasTooltip = hasGhost && ((tooltip_desc && tooltip_desc.trim() !== "") || hasTooltipCta);

  return (
    <section className={styles.section}>
      <div className={styles.content}>
        {hasBadge ? <span className={styles.badge}>{badge}</span> : null}
        {title ? <h2 className={styles.title}>{title}</h2> : null}
        {hasDesc ? (
          <HtmlText html={desc} className={styles.desc} />
        ) : null}
        {(hasPrimary || hasGhost) ? (
          <div className={styles.actions}>
            {hasPrimary ? (
              <a href={primaryUrl} className={styles.btnPrimary}>
                {primaryLabel}
              </a>
            ) : null}
            {hasGhost ? (
              <div className={styles.ghostWrap}>
                <span className={styles.btnGhost} role="button" tabIndex={0}>
                  <span className={styles.btnGhostLabel}>{ghost_label}</span>
                  {ghost_tag && ghost_tag.trim() !== "" ? (
                    <span className={styles.btnTag}>{ghost_tag}</span>
                  ) : null}
                </span>
                {hasTooltip ? (
                  <div className={styles.tooltip} role="tooltip">
                    {tooltip_desc && tooltip_desc.trim() !== "" ? (
                      <p className={styles.tooltipDesc}>{tooltip_desc}</p>
                    ) : null}
                    {hasTooltipCta ? (
                      <a href={tooltip_cta_url} className={styles.tooltipCta}>
                        {tooltip_cta_label}
                      </a>
                    ) : null}
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
      {image ? <img src={image} alt="" className={styles.image} loading="lazy" /> : null}
    </section>
  );
}
